import type {
  ApiContext,
  ApiNotAuthorized,
  CardInfo,
  CatalogChanged,
  InvalidFormField,
  MarketNotLinked,
  Scan,
  ScanCandidateUnknown,
  ScanConfirmation,
  ScanConfirmOptions,
  ScanEdit,
  ScanMarketRefresh,
  ScanNotFound,
  ScanNotReviewable,
  ScanStart,
  StoreNotFound,
} from "@hobbyshopify/backend-client";
import { newScanAttemptId } from "@hobbyshopify/backend-client";
import type { AsyncResult } from "operation-result.js";
import type { AuthContextProvider, AuthHandler } from "trunk.js";
import { applyScanEvent, initialScanProgress, type ScanProgress } from "../primitives/scan-progress";
import { shopEntryId } from "../primitives/workspace";
import type { CustomersRepository } from "../repositories/customers-repository";
import type { PendingCartsRepository } from "../repositories/pending-carts-repository";
import type { ReviewQueueRepository } from "../repositories/review-queue-repository";
import type { BackendApi } from "./api";

export function loadScan(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  scanId: string;
}): AsyncResult<Scan, ApiNotAuthorized | StoreNotFound | ScanNotFound> {
  return o.authHandler.callApi((ctx) => o.client.scans.get(ctx, o.shopId, o.scanId, { enableNoMatch: true }));
}

/** Asks the source for the shared estimate of a pending sealed scan's product. The asking price stays. */
export function refreshScanMarketPrice(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  scanId: string;
}): AsyncResult<
  ScanMarketRefresh,
  ApiNotAuthorized | StoreNotFound | ScanNotFound | ScanNotReviewable | MarketNotLinked
> {
  return o.authHandler.callApi((ctx) => o.client.scans.refreshMarketPrice(ctx, o.shopId, o.scanId));
}

/**
 * Saves the reading while the reviewer works, so the match and the price follow
 * the correction instead of waiting for confirm. The answer says whether the
 * edit forced a fresh pricing lookup.
 */
export function saveScanDraft(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  scanId: string;
  corrections: Partial<CardInfo> | Record<string, unknown>;
  /** false saves without a pricing lookup, true runs one; omitted, the edit decides. */
  search?: boolean;
  /** The reviewer's asking price; null hands it back to the estimate. */
  priceCents?: number | null;
}): AsyncResult<ScanEdit, ApiNotAuthorized | StoreNotFound | ScanNotFound | ScanNotReviewable> {
  const options = {
    ...(o.search !== undefined ? { search: o.search } : {}),
    ...(o.priceCents !== undefined ? { priceCents: o.priceCents } : {}),
  };
  return o.authHandler.callApi((ctx) =>
    Object.keys(options).length > 0
      ? o.client.scans.edit(ctx, o.shopId, o.scanId, o.corrections, options)
      : o.client.scans.edit(ctx, o.shopId, o.scanId, o.corrections),
  );
}

/** Makes one of the scan's candidates the pick. No lookup follows; the scan comes back re-matched. */
export function pickScanCandidate(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  scanId: string;
  productId: string;
}): AsyncResult<ScanEdit, ApiNotAuthorized | StoreNotFound | ScanNotFound | ScanNotReviewable | ScanCandidateUnknown> {
  return o.authHandler.callApi((ctx) => o.client.scans.pick(ctx, o.shopId, o.scanId, o.productId));
}

/** Settles a catalogue dispute: the scan becomes that catalogue product, priced like a catalogue hit. */
export function pickCatalogProduct(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  scanId: string;
  catalogProductId: string;
}): AsyncResult<ScanEdit, ApiNotAuthorized | StoreNotFound | ScanNotFound | ScanNotReviewable | ScanCandidateUnknown> {
  return o.authHandler.callApi((ctx) => o.client.scans.pickCatalog(ctx, o.shopId, o.scanId, o.catalogProductId));
}

/** What a confirm or reject touches elsewhere: the lists that showed the scan. */
export interface ScanReviewSideEffects {
  reviewQueueRepository: ReviewQueueRepository;
  customersRepository: CustomersRepository;
}

/** Confirms a scan as a card: the only path that creates one. */
export async function confirmScan(
  o: ScanReviewSideEffects & {
    client: BackendApi;
    authHandler: AuthHandler<ApiContext>;
    shopId: string;
    scanId: string;
    body: ScanConfirmOptions;
  },
): AsyncResult<
  ScanConfirmation,
  ApiNotAuthorized | StoreNotFound | ScanNotFound | ScanNotReviewable | CatalogChanged | InvalidFormField
> {
  const r = await o.authHandler.callApi((ctx) => o.client.scans.confirm(ctx, o.shopId, o.scanId, o.body));
  if (r.ok) markReviewed(o);
  return r;
}

export async function rejectScan(
  o: ScanReviewSideEffects & {
    client: BackendApi;
    authHandler: AuthHandler<ApiContext>;
    shopId: string;
    scanId: string;
  },
): AsyncResult<void, ApiNotAuthorized | StoreNotFound | ScanNotFound> {
  const r = await o.authHandler.callApi((ctx) => o.client.scans.reject(ctx, o.shopId, o.scanId));
  if (r.ok) markReviewed(o);
  return r;
}

/**
 * Deletes a customer's confirmed scan. The backend takes its quote out of
 * their open carts, deleting a cart it empties, so the carts are stale too.
 */
export async function deleteScan(
  o: ScanReviewSideEffects & {
    client: BackendApi;
    authHandler: AuthHandler<ApiContext>;
    pendingCartsRepository: PendingCartsRepository;
    shopId: string;
    scanId: string;
  },
): AsyncResult<void, ApiNotAuthorized | StoreNotFound | ScanNotFound> {
  const r = await o.authHandler.callApi((ctx) => o.client.scans.remove(ctx, o.shopId, o.scanId));
  if (r.ok) {
    markReviewed(o);
    o.pendingCartsRepository.markStale();
  }
  return r;
}

function markReviewed(o: ScanReviewSideEffects): void {
  o.reviewQueueRepository.markStale();
  o.customersRepository.markStale();
}

type ScanStreamOptions = {
  client: BackendApi;
  session: AuthContextProvider<ApiContext>;
  shopId: string;
  /** The counter the card was scanned at; the scan is filed under it. */
  locationId: string | null;
  /** The photos, and any barcode or kind the merchant already gave. */
  images: ScanStart;
  onProgress?: (progress: ScanProgress) => void;
  signal?: AbortSignal;
};

/**
 * Streams one scan, reporting progress as it goes, and answers with its last state: completed, failed (a
 * `scan.error` frame: by then the response headers are long gone), or still scanning when the stream ended early.
 * Throws only when the stream itself fails, to open or on the way.
 *
 * A stream, not a `Result`, so it takes the session directly rather than the
 * `AuthHandler`: there is no single answer a 401 could be retried into.
 *
 * Aborting stops the listening, not the scan: the backend links the ShopScan
 * before the pipeline runs, so it still completes and persists.
 */
export async function streamScan(o: ScanStreamOptions): Promise<ScanProgress> {
  const ctx = await o.session.authorizeCall();
  let state: ScanProgress = { ...initialScanProgress(), status: "scanning" };
  o.onProgress?.(state);

  for await (const event of o.client.scans.startStream(
    ctx,
    shopEntryId(o.shopId, o.locationId),
    { ...o.images, enableNoMatch: true, attemptId: o.images.attemptId ?? newScanAttemptId() },
    {
      signal: o.signal,
    },
  )) {
    state = applyScanEvent(state, event);
    o.onProgress?.(state);
  }
  return state;
}
